import React from "react";
// Components
import { TextInput } from "../../../components/TextInput/TextInput";
import { RadioSelectInput } from "../../../components/RadioSelectInput/RadioSelectInput";
import { Typography } from "../../../components/Typography/Typography";
import { TFunction } from "i18next";
import { ImageUploader } from "../../../components/ImageUploader/ImageUploader";
import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { TInsuranceInfoSchema } from "../intakeFormState";

type BCBSIntakeFormInsuranceInfoProps = {
  register: UseFormRegister<TInsuranceInfoSchema>;
  t: TFunction<"translation", undefined>;
  setValue: UseFormSetValue<TInsuranceInfoSchema>;
  errors: FieldErrors<TInsuranceInfoSchema>;
  setIsChildPolicyHolder: (value: string) => void;
  isChildPolicyHolder: string | null;
  setFrontInsuranceImageFile: (file: File | null) => void;
  setBackInsuranceImageFile: (file: File | null) => void;
  frontInsuranceImageFile: File | null;
  backInsuranceImageFile: File | null;
};

export const BCBSIntakeFormInsuranceInfo = React.memo(
  ({
    register,
    t,
    errors,
    setIsChildPolicyHolder,
    isChildPolicyHolder,
    setFrontInsuranceImageFile,
    setBackInsuranceImageFile,
    frontInsuranceImageFile,
    backInsuranceImageFile,
  }: BCBSIntakeFormInsuranceInfoProps) => {
    return (
      <>
        {/* Is the child the policy holder? */}
        <div className="mb-4 mt-4 flex flex-col">
          <RadioSelectInput
            {...register("isChildPolicyHolder")}
            label={t("intakeInsuranceInfo.isChildPolicyHolder")}
            isRequired
            choices={[
              { label: t("yes"), value: "true" },
              { label: t("no"), value: "false" },
            ]}
            hookFormFieldError={errors.isChildPolicyHolder}
            onChange={(event) => setIsChildPolicyHolder(event.target.value)}
          />
        </div>

        {/* If child is not the policy holder, show additional subscriber fields */}
        {isChildPolicyHolder === "false" && (
          <>
            {/* Subscriber first name */}
            <div className="mb-4 mt-4 flex flex-col">
              <TextInput
                {...register("subscriberFirstName")}
                isRequired
                label={t("intakeInsuranceInfo.subscriberFirstName")}
                hookFormFieldError={errors.subscriberFirstName}
              />
            </div>

            {/* Subscriber last name */}
            <div className="mb-4 mt-4 flex flex-col">
              <TextInput
                {...register("subscriberLastName")}
                isRequired
                label={t("intakeInsuranceInfo.subscriberLastName")}
                hookFormFieldError={errors.subscriberLastName}
              />
            </div>

            {/* Subscriber DOB */}
            <div className="mb-4 mt-4 flex flex-col">
              <TextInput
                {...register("subscriberDOB")}
                isRequired
                type="date"
                label={t("intakeInsuranceInfo.subscriberDOB")}
                hookFormFieldError={errors.subscriberDOB}
              />
            </div>

            {/* Subscriber ID number */}
            <div className="mb-4 mt-4 flex flex-col">
              <TextInput
                {...register("subscriberNumber")}
                isRequired
                label={t("intakeInsuranceInfo.subscriberNumber")}
                hookFormFieldError={errors.subscriberNumber}
              />
            </div>

            {/* Group number field (optional) */}
            <div className="mb-4 mt-4 flex flex-col">
              <TextInput
                {...register("groupNumber")}
                isRequired={false}
                label={t("intakeInsuranceInfo.groupNumber")}
                hookFormFieldError={errors.groupNumber}
              />
            </div>
          </>
        )}

        {/* Upload insurance card images */}
        {/* front */}
        <Typography className="mb-4 mt-4 flex flex-col">{t("intakeInsuranceInfo.uploadInsuranceCard")}</Typography>
        <input type="hidden" name="insuranceCardFront" />
        <ImageUploader
          label={t("intakeInsuranceInfo.uploadInsuranceCardFront")}
          onFileUpload={(file) => {
            setFrontInsuranceImageFile(file);
          }}
          uploadedFile={frontInsuranceImageFile}
          errorMessage={!frontInsuranceImageFile ? t("intakeInsuranceInfo.imageRequiredError") : null}
        />
        {/* back */}
        <input type="hidden" name="insuranceCardBack" />
        <ImageUploader
          label={t("intakeInsuranceInfo.uploadInsuranceCardBack")}
          onFileUpload={(file) => {
            setBackInsuranceImageFile(file);
          }}
          uploadedFile={backInsuranceImageFile}
          errorMessage={!backInsuranceImageFile ? t("intakeInsuranceInfo.imageRequiredError") : null}
        />

        {/* Legal/disclaimer copy */}
        <Typography>{t("intakeInsuranceInfo.insuranceDisclaimer")}</Typography>
      </>
    );
  },
);

BCBSIntakeFormInsuranceInfo.displayName = "BCBSIntakeFormInsuranceInfo";
